import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, animate } from 'motion/react';
import { SportType, AthleteProfile } from '../types';
import { X, Plus, Minus, Trophy, Sparkles, Check, Mic, MicOff, Volume2, AlertCircle, RefreshCw, Zap, Save, MapPin } from 'lucide-react';
import { GpsMapThumbnail } from './regional/GpsMapThumbnail';
import { triggerHaptic } from '../utils/haptics';

// Count-up Animated Number Component for Framer Motion summary cards
export const CountUpNumber: React.FC<{ value: number; prefix?: string; suffix?: string; duration?: number }> = ({
  value,
  prefix = '',
  suffix = '',
  duration = 1.2,
}) => {
  const count = useMotionValue(0);
  const [displayValue, setDisplayValue] = useState(`${prefix}0${suffix}`);

  useEffect(() => {
    const controls = animate(count, value, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => {
        setDisplayValue(`${prefix}${Math.round(latest)}${suffix}`);
      },
    });
    return () => controls.stop();
  }, [value, prefix, suffix, duration]);

  return <span>{displayValue}</span>;
};

// Declare SpeechRecognition for Web Speech API TypeScript support
declare global {
  interface Window {
    SpeechRecognition?: any;
    webkitSpeechRecognition?: any;
  }
}

interface StatLogModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitStats: (sport: SportType, statsData: any) => void;
  user?: AthleteProfile;
  prefilledData?: any;
  activeSportFilter?: SportType;
}

const STAT_DRAFT_KEY = 'stat_log_modal_draft';

export const StatLogModal: React.FC<StatLogModalProps> = ({
  isOpen,
  onClose,
  onSubmitStats,
  user,
  prefilledData,
  activeSportFilter,
}) => {
  const [sport, setSport] = useState<SportType>(activeSportFilter || user?.primarySport || 'volleyball');
  const [autoDetectedNotice, setAutoDetectedNotice] = useState<string | null>(null);

  // Basketball quick counters
  const [pts, setPts] = useState(18);
  const [ast, setAst] = useState(6);
  const [reb, setReb] = useState(4);
  const [stl, setStl] = useState(2);
  const [blk, setBlk] = useState(1);
  const [threeMade, setThreeMade] = useState(3);
  const [isWin, setIsWin] = useState(true);

  // Baseball quick counters
  const [hits, setHits] = useState(2);
  const [atBats, setAtBats] = useState(4);
  const [rbis, setRbis] = useState(3);
  const [hrs, setHrs] = useState(1);

  // Softball quick counters
  const [sbHits, setSbHits] = useState(3);
  const [sbAtBats, setSbAtBats] = useState(4);
  const [sbRbis, setSbRbis] = useState(2);
  const [sbSb, setSbSb] = useState(1);

  // Pickleball quick counters
  const [pbDinks, setPbDinks] = useState(24);
  const [pbAces, setPbAces] = useState(5);

  // Soccer quick counters
  const [goals, setGoals] = useState(1);
  const [soccerAssists, setSoccerAssists] = useState(1);

  // Volleyball quick counters
  const [kills, setKills] = useState(8);
  const [vbAces, setVbAces] = useState(4);
  const [vbBlocks, setVbBlocks] = useState(3);
  const [vbDigs, setVbDigs] = useState(6);
  const [vbAssists, setVbAssists] = useState(12);

  // Football quick counters
  const [passYds, setPassYds] = useState(180);
  const [tds, setTds] = useState(2);

  // Tennis quick counters
  const [tennisAces, setTennisAces] = useState(5);
  const [bpWon, setBpWon] = useState(3);

  // Re-log prefill mode banner
  const [relogNotice, setRelogNotice] = useState<string | null>(null);

  // Draft persistence & summary animation states
  const [draftRestored, setDraftRestored] = useState(false);
  const [showSummaryScreen, setShowSummaryScreen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedData, setSubmittedData] = useState<{ pts: number; ast: number; reb: number; xp: number; geolocation?: any } | null>(null);

  // Restore draft or handle prefilled game duplication
  useEffect(() => {
    if (!isOpen) return;
    setShowSummaryScreen(false);

    if (prefilledData) {
      if (prefilledData.sport) setSport(prefilledData.sport);
      if (prefilledData.pts !== undefined) setPts(prefilledData.pts);
      if (prefilledData.points !== undefined) setPts(prefilledData.points);
      if (prefilledData.ast !== undefined) setAst(prefilledData.ast);
      if (prefilledData.assists !== undefined) setAst(prefilledData.assists);
      if (prefilledData.reb !== undefined) setReb(prefilledData.reb);
      if (prefilledData.rebounds !== undefined) setReb(prefilledData.rebounds);
      if (prefilledData.stl !== undefined) setStl(prefilledData.stl);
      if (prefilledData.steals !== undefined) setStl(prefilledData.steals);
      if (prefilledData.blk !== undefined) setBlk(prefilledData.blk);
      if (prefilledData.blocks !== undefined) setBlk(prefilledData.blocks);
      if (prefilledData.threePM !== undefined) setThreeMade(prefilledData.threePM);
      if (prefilledData.isWin !== undefined) setIsWin(prefilledData.isWin);
      if (prefilledData.result === 'W') setIsWin(true);
      if (prefilledData.result === 'L') setIsWin(false);
      if (prefilledData.kills !== undefined) setKills(prefilledData.kills);
      if (prefilledData.digs !== undefined) setVbDigs(prefilledData.digs);
      if (prefilledData.aces !== undefined) setVbAces(prefilledData.aces);
      if (prefilledData.goals !== undefined) setGoals(prefilledData.goals);
      if (prefilledData.hits !== undefined) setHits(prefilledData.hits);
      if (prefilledData.atBats !== undefined) setAtBats(prefilledData.atBats);

      setRelogNotice(`Re-logging game vs ${prefilledData.opponent || 'Opponent'} (${prefilledData.location || 'Local Court'})`);
      return;
    }

    setRelogNotice(null);
    
    // Auto-detect sport from activeSportFilter or primarySport
    const targetSport = activeSportFilter || user?.primarySport || 'volleyball';
    setSport(targetSport);
    if (activeSportFilter) {
      setAutoDetectedNotice(`Auto-detected sport: ${activeSportFilter.toUpperCase()} (from active filter)`);
      setTimeout(() => setAutoDetectedNotice(null), 4500);
    } else {
      setAutoDetectedNotice(null);
    }

    const saved = localStorage.getItem(STAT_DRAFT_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only override sport if activeSportFilter wasn't explicitly active
        if (parsed.sport && !activeSportFilter) setSport(parsed.sport);
        if (parsed.pts !== undefined) setPts(parsed.pts);
        if (parsed.ast !== undefined) setAst(parsed.ast);
        if (parsed.reb !== undefined) setReb(parsed.reb);
        if (parsed.stl !== undefined) setStl(parsed.stl);
        if (parsed.blk !== undefined) setBlk(parsed.blk);
        if (parsed.threeMade !== undefined) setThreeMade(parsed.threeMade);
        if (parsed.isWin !== undefined) setIsWin(parsed.isWin);
        setDraftRestored(true);
        setTimeout(() => setDraftRestored(false), 3000);
      } catch (err) {
        console.error('Failed to parse stat draft:', err);
      }
    }
  }, [isOpen, prefilledData, activeSportFilter, user?.primarySport]);

  // Periodically save draft on counter updates
  useEffect(() => {
    if (!isOpen || showSummaryScreen) return;
    const draftData = {
      sport,
      pts,
      ast,
      reb,
      stl,
      blk,
      threeMade,
      isWin,
      hits,
      atBats,
      rbis,
      hrs,
      savedAt: new Date().toISOString(),
    };
    localStorage.setItem(STAT_DRAFT_KEY, JSON.stringify(draftData));
  }, [isOpen, showSummaryScreen, sport, pts, ast, reb, stl, blk, threeMade, isWin, hits, atBats, rbis, hrs]);

  const handleClearDraft = () => {
    localStorage.removeItem(STAT_DRAFT_KEY);
    setPts(18);
    setAst(6);
    setReb(4);
    setDraftRestored(false);
  };

  // Speech Recognition States
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [recognizedStats, setRecognizedStats] = useState<string[]>([]);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);

  // Cleanup speech recognition on unmount or close
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Parse spoken text into numerical stats
  const parseSpokenStats = (text: string) => {
    const lower = text.toLowerCase();
    const parsedMessages: string[] = [];

    // Helper regex to extract numbers preceding or following keywords
    const extractNumber = (keywords: string[]): number | null => {
      for (const kw of keywords) {
        // Pattern e.g. "10 points" or "points 10" or "ten points"
        const pattern1 = new RegExp(`(\\d+)\\s*(?:${kw})`, 'i');
        const pattern2 = new RegExp(`(?:${kw})\\s*(\\d+)`, 'i');
        const m1 = lower.match(pattern1);
        if (m1) return parseInt(m1[1], 10);
        const m2 = lower.match(pattern2);
        if (m2) return parseInt(m2[1], 10);
      }
      return null;
    };

    // Check Outcome
    if (lower.includes('win') || lower.includes('won') || lower.includes('victory')) {
      setIsWin(true);
      parsedMessages.push('Outcome: WIN');
    } else if (lower.includes('loss') || lower.includes('lost') || lower.includes('defeat')) {
      setIsWin(false);
      parsedMessages.push('Outcome: LOSS');
    }

    if (sport === 'basketball') {
      const p = extractNumber(['points', 'point', 'pts', 'pt']);
      if (p !== null) { setPts(p); parsedMessages.push(`${p} Points`); }

      const a = extractNumber(['assists', 'assist', 'ast']);
      if (a !== null) { setAst(a); parsedMessages.push(`${a} Assists`); }

      const r = extractNumber(['rebounds', 'rebound', 'reb', 'boards']);
      if (r !== null) { setReb(r); parsedMessages.push(`${r} Rebounds`); }

      const t = extractNumber(['threes', 'three pointers', '3 pointers', '3 pointers made', '3pt', '3s']);
      if (t !== null) { setThreeMade(t); parsedMessages.push(`${t} Threes`); }

      const s = extractNumber(['steals', 'steal', 'stl']);
      if (s !== null) { setStl(s); parsedMessages.push(`${s} Steals`); }

      const b = extractNumber(['blocks', 'block', 'blk']);
      if (b !== null) { setBlk(b); parsedMessages.push(`${b} Blocks`); }
    } else if (sport === 'baseball' || sport === 'softball') {
      const h = extractNumber(['hits', 'hit', 'base hits']);
      if (h !== null) {
        if (sport === 'baseball') setHits(h); else setSbHits(h);
        parsedMessages.push(`${h} Hits`);
      }
      const ab = extractNumber(['at bats', 'at-bats', 'at bat', 'abs']);
      if (ab !== null) {
        if (sport === 'baseball') setAtBats(ab); else setSbAtBats(ab);
        parsedMessages.push(`${ab} At-Bats`);
      }
      const rbi = extractNumber(['rbis', 'rbi', 'runs batted in']);
      if (rbi !== null) {
        if (sport === 'baseball') setRbis(rbi); else setSbRbis(rbi);
        parsedMessages.push(`${rbi} RBIs`);
      }
      const hr = extractNumber(['home runs', 'homeruns', 'homers', 'hr']);
      if (hr !== null) { setHrs(hr); parsedMessages.push(`${hr} Home Runs`); }
      const sb = extractNumber(['stolen bases', 'stolen base', 'steals']);
      if (sb !== null) { setSbSb(sb); parsedMessages.push(`${sb} Stolen Bases`); }
    } else if (sport === 'soccer') {
      const g = extractNumber(['goals', 'goal']);
      if (g !== null) { setGoals(g); parsedMessages.push(`${g} Goals`); }
      const a = extractNumber(['assists', 'assist']);
      if (a !== null) { setSoccerAssists(a); parsedMessages.push(`${a} Assists`); }
    } else if (sport === 'pickleball') {
      const d = extractNumber(['dinks', 'kitchen dinks', 'dink']);
      if (d !== null) { setPbDinks(d); parsedMessages.push(`${d} Dinks`); }
      const ac = extractNumber(['aces', 'ace']);
      if (ac !== null) { setPbAces(ac); parsedMessages.push(`${ac} Aces`); }
    } else if (sport === 'volleyball') {
      const k = extractNumber(['kills', 'kill', 'spikes']);
      if (k !== null) { setKills(k); parsedMessages.push(`${k} Kills`); }
      const ac = extractNumber(['aces', 'ace', 'service aces']);
      if (ac !== null) { setVbAces(ac); parsedMessages.push(`${ac} Aces`); }
      const blk = extractNumber(['blocks', 'block', 'net blocks']);
      if (blk !== null) { setVbBlocks(blk); parsedMessages.push(`${blk} Blocks`); }
      const dg = extractNumber(['digs', 'dig', 'ground digs']);
      if (dg !== null) { setVbDigs(dg); parsedMessages.push(`${dg} Digs`); }
      const ast = extractNumber(['assists', 'assist', 'setting assists']);
      if (ast !== null) { setVbAssists(ast); parsedMessages.push(`${ast} Assists`); }
    } else if (sport === 'football') {
      const yds = extractNumber(['passing yards', 'yards', 'yds', 'yardage']);
      if (yds !== null) { setPassYds(yds); parsedMessages.push(`${yds} Passing Yards`); }
      const td = extractNumber(['touchdowns', 'touchdown', 'tds', 'td']);
      if (td !== null) { setTds(td); parsedMessages.push(`${td} Touchdowns`); }
    } else if (sport === 'tennis') {
      const ac = extractNumber(['aces', 'ace']);
      if (ac !== null) { setTennisAces(ac); parsedMessages.push(`${ac} Aces`); }
      const bp = extractNumber(['break points', 'break points won']);
      if (bp !== null) { setBpWon(bp); parsedMessages.push(`${bp} Break Points`); }
    }

    if (parsedMessages.length > 0) {
      setRecognizedStats(parsedMessages);
    }
  };

  const startVoiceRecognition = () => {
    triggerHaptic('medium');
    setVoiceError(null);
    setTranscript('');
    setRecognizedStats([]);

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setVoiceError('Speech recognition is not supported in this browser. You can still log stats manually.');
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: any) => {
        let currentTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          currentTranscript += event.results[i][0].transcript;
        }
        setTranscript(currentTranscript);
        parseSpokenStats(currentTranscript);
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          setVoiceError('Microphone permission was denied. Please allow mic access.');
        } else {
          setVoiceError(`Voice error: ${event.error}`);
        }
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error('Error starting recognition:', err);
      setIsListening(false);
      setVoiceError('Failed to initialize microphone.');
    }
  };

  const stopVoiceRecognition = () => {
    triggerHaptic('medium');
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    triggerHaptic('success');

    localStorage.removeItem(STAT_DRAFT_KEY);

    const userCity = user?.registeredCity || 'New York';
    const userState = user?.registeredState || 'NY';

    const processSubmission = (lat: number, lng: number, isRealGps: boolean) => {
      const geoObj = {
        latitude: Number(lat.toFixed(4)),
        longitude: Number(lng.toFixed(4)),
        city: userCity,
        state: userState,
        locationName: isRealGps
          ? `${userCity} GPS Arena (${lat.toFixed(2)}, ${lng.toFixed(2)})`
          : `${userCity} Sports Court Arena`,
        verifiedAt: new Date().toISOString(),
      };

      let statsObj: any = { geolocation: geoObj };
      if (sport === 'basketball') {
        statsObj = { ...statsObj, pts, ast, reb, stl, blk, threeMade, isWin };
      } else if (sport === 'softball') {
        statsObj = { ...statsObj, hits: sbHits, atBats: sbAtBats, rbis: sbRbis, stolenBases: sbSb, isWin };
      } else if (sport === 'pickleball') {
        statsObj = { ...statsObj, dinks: pbDinks, aces: pbAces, isWin };
      } else if (sport === 'soccer') {
        statsObj = { ...statsObj, goals, assists: soccerAssists, isWin };
      } else if (sport === 'volleyball') {
        statsObj = { ...statsObj, kills, aces: vbAces, blocks: vbBlocks, digs: vbDigs, assists: vbAssists, isWin };
      } else if (sport === 'football') {
        statsObj = { ...statsObj, passingYards: passYds, touchdowns: tds, isWin };
      } else if (sport === 'tennis') {
        statsObj = { ...statsObj, aces: tennisAces, breakPointsWon: bpWon, isWin };
      } else {
        statsObj = { ...statsObj, hits, atBats, rbis, hrs, isWin };
      }

      onSubmitStats(sport, statsObj);

      setSubmittedData({
        pts: statsObj.pts || statsObj.goals || statsObj.hits || statsObj.kills || 18,
        ast: statsObj.ast || statsObj.assists || statsObj.rbis || 6,
        reb: statsObj.reb || statsObj.dinks || statsObj.aces || 4,
        xp: 250,
        geolocation: geoObj,
      });

      setTimeout(() => {
        setIsSubmitting(false);
        setShowSummaryScreen(true);
      }, 400);
    };

    if (typeof navigator !== 'undefined' && 'geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          processSubmission(pos.coords.latitude, pos.coords.longitude, true);
        },
        (err) => {
          console.warn('GPS location fallback:', err.message);
          const fallbackLat = 34.0522 + (Math.random() - 0.5) * 0.04;
          const fallbackLng = -118.2437 + (Math.random() - 0.5) * 0.04;
          processSubmission(fallbackLat, fallbackLng, false);
        },
        { timeout: 2500, maximumAge: 60000 }
      );
    } else {
      const fallbackLat = 34.0522 + (Math.random() - 0.5) * 0.04;
      const fallbackLng = -118.2437 + (Math.random() - 0.5) * 0.04;
      processSubmission(fallbackLat, fallbackLng, false);
    }
  };

  const handleFinishSummary = () => {
    setShowSummaryScreen(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-indigo-950/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-indigo-900/90 text-white rounded-[2.5rem] border border-white/10 shadow-2xl w-full max-w-lg overflow-hidden space-y-6 p-6 relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 text-indigo-200 hover:text-white rounded-full bg-indigo-950/80 border border-white/10 transition z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {showSummaryScreen ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
            className="space-y-6 text-center py-4"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.1, type: 'spring', stiffness: 220, damping: 15 }}
              className="w-16 h-16 bg-lime-400 text-black rounded-full flex items-center justify-center mx-auto shadow-lg shadow-lime-400/30"
            >
              <Check className="w-10 h-10 stroke-[3]" />
            </motion.div>

            <div className="space-y-1">
              <h3 className="text-2xl font-black italic uppercase text-white tracking-wide">
                Stats Logged & Verified! 🏐
              </h3>
              <p className="text-xs text-lime-400 font-bold uppercase tracking-wider">
                Your {sport.toUpperCase()} performance & daily streak updated
              </p>
            </div>

            {/* Framer Motion Summary Cards with Sport-Specific Metrics */}
            <div className="grid grid-cols-2 gap-3 pt-2">
              {sport === 'volleyball' ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-indigo-950/90 p-4 rounded-2xl border border-lime-400/30 flex flex-col items-center shadow-md"
                  >
                    <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Spike Kills</span>
                    <span className="text-3xl font-black italic text-lime-400 mt-1">
                      <CountUpNumber value={kills} suffix=" KILLS" />
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-indigo-950/90 p-4 rounded-2xl border border-cyan-400/30 flex flex-col items-center shadow-md"
                  >
                    <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Ground Digs</span>
                    <span className="text-3xl font-black italic text-cyan-400 mt-1">
                      <CountUpNumber value={vbDigs} suffix=" DIGS" />
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-indigo-950/90 p-4 rounded-2xl border border-amber-400/30 flex flex-col items-center shadow-md"
                  >
                    <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Blocks & Aces</span>
                    <span className="text-2xl font-black italic text-amber-300 mt-1">
                      {vbBlocks} BLK / {vbAces} ACE
                    </span>
                  </motion.div>
                </>
              ) : sport === 'soccer' ? (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-indigo-950/90 p-4 rounded-2xl border border-lime-400/30 flex flex-col items-center shadow-md"
                  >
                    <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Goals Scored</span>
                    <span className="text-3xl font-black italic text-lime-400 mt-1">
                      <CountUpNumber value={goals} suffix=" GOALS" />
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-indigo-950/90 p-4 rounded-2xl border border-cyan-400/30 flex flex-col items-center shadow-md"
                  >
                    <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Key Assists</span>
                    <span className="text-3xl font-black italic text-cyan-400 mt-1">
                      <CountUpNumber value={soccerAssists} suffix=" AST" />
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-indigo-950/90 p-4 rounded-2xl border border-indigo-400/30 flex flex-col items-center shadow-md"
                  >
                    <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Match Result</span>
                    <span className="text-2xl font-black italic text-white mt-1">
                      {isWin ? 'WIN 🏆' : 'LOSS ❌'}
                    </span>
                  </motion.div>
                </>
              ) : (
                <>
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-indigo-950/90 p-4 rounded-2xl border border-lime-400/30 flex flex-col items-center shadow-md"
                  >
                    <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Points</span>
                    <span className="text-3xl font-black italic text-lime-400 mt-1">
                      <CountUpNumber value={submittedData?.pts || pts} suffix=" PTS" />
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-indigo-950/90 p-4 rounded-2xl border border-cyan-400/30 flex flex-col items-center shadow-md"
                  >
                    <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Assists</span>
                    <span className="text-3xl font-black italic text-cyan-400 mt-1">
                      <CountUpNumber value={submittedData?.ast || ast} suffix=" AST" />
                    </span>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-indigo-950/90 p-4 rounded-2xl border border-indigo-400/30 flex flex-col items-center shadow-md"
                  >
                    <span className="text-[10px] text-indigo-300 font-black uppercase tracking-wider">Rebounds</span>
                    <span className="text-3xl font-black italic text-indigo-200 mt-1">
                      <CountUpNumber value={submittedData?.reb || reb} suffix=" REB" />
                    </span>
                  </motion.div>
                </>
              )}

              {/* XP Bonus Card */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 p-4 rounded-2xl border border-amber-400/40 flex flex-col items-center shadow-md"
              >
                <span className="text-[10px] text-amber-300 font-black uppercase tracking-wider">XP Bonus Earned</span>
                <span className="text-3xl font-black italic text-amber-400 mt-1">
                  <CountUpNumber value={250} prefix="+" suffix=" XP" />
                </span>
              </motion.div>
            </div>

            {/* GPS Telemetry Map Confirmation Thumbnail */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.55 }}
              className="space-y-1.5"
            >
              <div className="flex items-center justify-between text-[10px] font-mono font-black uppercase text-lime-400 px-1">
                <span className="flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-lime-400" />
                  <span>GPS VERIFIED LOG LOCATION</span>
                </span>
                <span className="text-indigo-300/80">Tap to expand map</span>
              </div>
              <GpsMapThumbnail
                latitude={submittedData?.geolocation?.latitude || 34.0522}
                longitude={submittedData?.geolocation?.longitude || -118.2437}
                locationName={submittedData?.geolocation?.locationName || `${user?.registeredCity || 'Local'} Arena Court #1`}
                city={submittedData?.geolocation?.city || user?.registeredCity || 'Venice'}
                state={submittedData?.geolocation?.state || user?.registeredState || 'CA'}
                size="sm"
              />
            </motion.div>

            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              onClick={handleFinishSummary}
              className="w-full py-3.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-sm rounded-xl shadow-xl shadow-lime-400/20 transition flex items-center justify-center space-x-2"
            >
              <span>Return to Dashboard</span>
              <Sparkles className="w-4 h-4 fill-black" />
            </motion.button>
          </motion.div>
        ) : (
          <>
            {relogNotice && (
              <div className="bg-lime-400 text-black px-3.5 py-2 rounded-xl text-xs font-black uppercase font-mono flex items-center justify-between shadow-lg border border-lime-300 animate-fadeIn">
                <span className="flex items-center space-x-1.5">
                  <Sparkles className="w-4 h-4 fill-black" />
                  <span>✨ {relogNotice}</span>
                </span>
                <span className="text-[10px] bg-black text-lime-400 px-2 py-0.5 rounded font-mono">
                  DUPLICATING LOG
                </span>
              </div>
            )}

            {autoDetectedNotice && !relogNotice && (
              <div className="bg-cyan-400 text-black px-3.5 py-2 rounded-xl text-xs font-black uppercase font-mono flex items-center justify-between shadow-lg border border-cyan-300 animate-fadeIn">
                <span className="flex items-center space-x-1.5">
                  <Zap className="w-4 h-4 fill-black" />
                  <span>🎯 {autoDetectedNotice}</span>
                </span>
                <span className="text-[10px] bg-black text-cyan-300 px-2 py-0.5 rounded font-mono">
                  AUTO-DETECTED
                </span>
              </div>
            )}

            {draftRestored && (
              <div className="bg-lime-400/20 border border-lime-400/40 text-lime-300 px-3.5 py-2 rounded-xl text-xs font-bold flex items-center justify-between">
                <span className="flex items-center space-x-1.5">
                  <Check className="w-4 h-4 text-lime-400" />
                  <span>Draft restored from your last session</span>
                </span>
                <button
                  onClick={handleClearDraft}
                  className="text-[10px] text-lime-400 underline font-mono hover:text-white"
                >
                  Reset Form
                </button>
              </div>
            )}

        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Trophy className="w-6 h-6 text-lime-400" />
            <h2 className="text-xl font-black italic uppercase text-white">
              Log Recent Game Performance
            </h2>
          </div>
          <p className="text-xs text-indigo-200/70 font-semibold">
            Record game stats manually or speak directly to hands-free voice log stats!
          </p>
        </div>

        {/* Voice Recognition Control Section */}
        <div className="bg-indigo-950/90 p-4 rounded-3xl border border-lime-400/30 space-y-3 relative overflow-hidden shadow-inner">
          <div className="flex items-center justify-between">
            <span className="text-xs font-black italic uppercase text-lime-400 flex items-center space-x-1.5">
              <Mic className="w-4 h-4 text-lime-400" />
              <span>AI Voice Recognition Log</span>
            </span>

            {isListening ? (
              <button
                onClick={stopVoiceRecognition}
                type="button"
                className="px-3 py-1 bg-rose-500 hover:bg-rose-600 text-white text-[11px] font-black uppercase rounded-xl flex items-center space-x-1.5 animate-pulse"
              >
                <MicOff className="w-3.5 h-3.5" />
                <span>Stop Mic</span>
              </button>
            ) : (
              <button
                onClick={startVoiceRecognition}
                type="button"
                className="px-3.5 py-1.5 bg-lime-400 hover:bg-lime-300 text-black text-[11px] font-black italic uppercase rounded-xl shadow-md flex items-center space-x-1.5 transition"
              >
                <Mic className="w-3.5 h-3.5" />
                <span>Speak Stats 🎙️</span>
              </button>
            )}
          </div>

          {/* Transcript / Listening Visual Feedback */}
          {isListening && (
            <div className="p-3 bg-indigo-900/80 rounded-2xl border border-lime-400/50 flex items-center space-x-3 animate-pulse">
              <div className="w-3 h-3 rounded-full bg-lime-400 animate-ping" />
              <p className="text-xs text-lime-300 font-medium italic">
                {transcript ? `"${transcript}"` : 'Listening... Speak now (e.g. "22 points, 6 assists, 4 rebounds, win")'}
              </p>
            </div>
          )}

          {!isListening && transcript && (
            <div className="p-2.5 bg-indigo-900/60 rounded-2xl border border-white/10 text-xs text-indigo-200">
              <span className="text-[10px] text-indigo-300/60 font-black uppercase block">Last Voice Input:</span>
              <p className="italic text-white">"{transcript}"</p>
            </div>
          )}

          {/* Parsed Stats Badges */}
          {recognizedStats.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 pt-1">
              <span className="text-[10px] text-indigo-300/80 font-black uppercase mr-1">Parsed:</span>
              {recognizedStats.map((stat, idx) => (
                <span key={idx} className="px-2 py-0.5 bg-lime-400 text-black text-[10px] font-black uppercase rounded-lg flex items-center">
                  <Check className="w-3 h-3 mr-0.5 stroke-[3]" />
                  {stat}
                </span>
              ))}
            </div>
          )}

          {voiceError && (
            <div className="p-2 bg-rose-500/20 border border-rose-500/40 rounded-xl flex items-center space-x-2 text-xs text-rose-300">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-400" />
              <span>{voiceError}</span>
            </div>
          )}

          <div className="flex items-center justify-between text-[10px] text-indigo-300/60 pt-1">
            <span>💡 Example: <i>"24 points, 8 assists, 5 rebounds, win"</i></span>
            <button
              type="button"
              onClick={() => parseSpokenStats("24 points, 8 assists, 5 rebounds, 3 steals, win")}
              className="text-lime-400 hover:underline font-bold"
            >
              Try Sample Voice Log
            </button>
          </div>
        </div>

        {/* Sport Selector */}
        <div className="flex flex-wrap bg-indigo-950 p-1.5 rounded-2xl border border-white/10 gap-1">
          {[
            { id: 'basketball', label: 'Bball 🏀' },
            { id: 'baseball', label: 'Baseball ⚾' },
            { id: 'softball', label: 'Softball 🥎' },
            { id: 'pickleball', label: 'Pickleball 🏓' },
            { id: 'soccer', label: 'Soccer ⚽' },
            { id: 'volleyball', label: 'Volleyball 🏐' },
            { id: 'football', label: 'Football 🏈' },
            { id: 'tennis', label: 'Tennis 🎾' },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                triggerHaptic('light');
                setSport(s.id as SportType);
              }}
              className={`px-3 py-1.5 rounded-xl text-[11px] font-black italic uppercase transition ${
                sport === s.id ? 'bg-lime-400 text-black shadow-md' : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Win/Loss Check */}
          <div className="flex items-center justify-between p-3 bg-indigo-950/80 rounded-2xl border border-white/10">
            <span className="text-xs font-bold text-indigo-200">
              Game Outcome
            </span>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('medium');
                  setIsWin(true);
                }}
                className={`px-3 py-1 rounded-xl text-xs font-black italic uppercase transition ${
                  isWin ? 'bg-lime-400 text-black shadow-md' : 'bg-indigo-900 text-indigo-300'
                }`}
              >
                WIN 🏆
              </button>
              <button
                type="button"
                onClick={() => {
                  triggerHaptic('medium');
                  setIsWin(false);
                }}
                className={`px-3 py-1 rounded-xl text-xs font-black italic uppercase transition ${
                  !isWin ? 'bg-rose-500 text-white shadow-md' : 'bg-indigo-900 text-indigo-300'
                }`}
              >
                LOSS
              </button>
            </div>
          </div>

          {sport === 'basketball' ? (
            <div className="grid grid-cols-2 gap-4">
              <CounterItem label="Points (PTS)" value={pts} onChange={setPts} />
              <CounterItem label="Assists (AST)" value={ast} onChange={setAst} />
              <CounterItem label="Rebounds (REB)" value={reb} onChange={setReb} />
              <CounterItem label="3PT Made" value={threeMade} onChange={setThreeMade} />
              <CounterItem label="Steals (STL)" value={stl} onChange={setStl} />
              <CounterItem label="Blocks (BLK)" value={blk} onChange={setBlk} />
            </div>
          ) : sport === 'softball' ? (
            <div className="grid grid-cols-2 gap-4">
              <CounterItem label="Base Hits" value={sbHits} onChange={setSbHits} />
              <CounterItem label="At Bats" value={sbAtBats} onChange={setSbAtBats} />
              <CounterItem label="RBIs" value={sbRbis} onChange={setSbRbis} />
              <CounterItem label="Stolen Bases" value={sbSb} onChange={setSbSb} />
            </div>
          ) : sport === 'pickleball' ? (
            <div className="grid grid-cols-2 gap-4">
              <CounterItem label="Kitchen Dinks" value={pbDinks} onChange={setPbDinks} />
              <CounterItem label="Aces Served" value={pbAces} onChange={setPbAces} />
            </div>
          ) : sport === 'soccer' ? (
            <div className="grid grid-cols-2 gap-4">
              <CounterItem label="Goals Scored" value={goals} onChange={setGoals} />
              <CounterItem label="Assists" value={soccerAssists} onChange={setSoccerAssists} />
            </div>
          ) : sport === 'volleyball' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <CounterItem label="Spike Kills 🏐" value={kills} onChange={setKills} />
              <CounterItem label="Service Aces ⚡" value={vbAces} onChange={setVbAces} />
              <CounterItem label="Net Blocks 🛡️" value={vbBlocks} onChange={setVbBlocks} />
              <CounterItem label="Ground Digs 💥" value={vbDigs} onChange={setVbDigs} />
              <CounterItem label="Setting Assists 🎯" value={vbAssists} onChange={setVbAssists} />
            </div>
          ) : sport === 'football' ? (
            <div className="grid grid-cols-2 gap-4">
              <CounterItem label="Passing Yards" value={passYds} onChange={setPassYds} />
              <CounterItem label="Touchdowns" value={tds} onChange={setTds} />
            </div>
          ) : sport === 'tennis' ? (
            <div className="grid grid-cols-2 gap-4">
              <CounterItem label="Aces Served" value={tennisAces} onChange={setTennisAces} />
              <CounterItem label="Break Points Won" value={bpWon} onChange={setBpWon} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <CounterItem label="Base Hits" value={hits} onChange={setHits} />
              <CounterItem label="At Bats" value={atBats} onChange={setAtBats} />
              <CounterItem label="RBIs" value={rbis} onChange={setRbis} />
              <CounterItem label="Home Runs" value={hrs} onChange={setHrs} />
            </div>
          )}

          <div className="pt-2 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl text-xs font-bold text-indigo-200 hover:text-white"
            >
              Cancel
            </button>
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.93 }}
              animate={isSubmitting ? { scale: [1, 0.95, 1.05, 1], backgroundColor: '#a3e635' } : {}}
              transition={{ duration: 0.25 }}
              className="px-6 py-2.5 bg-lime-400 hover:bg-lime-300 disabled:opacity-80 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition flex items-center space-x-1.5 cursor-pointer relative overflow-hidden"
            >
              <AnimatePresence mode="wait">
                {isSubmitting ? (
                  <motion.div
                    key="submitting_state"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="flex items-center space-x-1.5"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 0.6, ease: 'linear' }}
                    >
                      <Sparkles className="w-4 h-4 text-black" />
                    </motion.div>
                    <span>Logging Stats & XP...</span>
                  </motion.div>
                ) : (
                  <motion.div
                    key="normal_state"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="flex items-center space-x-1.5"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Submit & Earn +200 XP</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </form>
      </>
    )}
      </div>
    </div>
  );
};

const CounterItem: React.FC<{ label: string; value: number; onChange: (v: number) => void }> = ({
  label,
  value,
  onChange,
}) => (
  <div className="p-3 rounded-2xl bg-indigo-950/80 border border-white/10 flex items-center justify-between">
    <span className="text-xs font-bold text-indigo-200">{label}</span>
    <div className="flex items-center space-x-2">
      <button
        type="button"
        onClick={() => {
          triggerHaptic('light');
          onChange(Math.max(0, value - 1));
        }}
        className="w-7 h-7 rounded-lg bg-indigo-900 text-white font-bold flex items-center justify-center hover:bg-indigo-800 border border-white/10"
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      <span className="w-6 text-center text-sm font-black italic text-white">{value}</span>
      <button
        type="button"
        onClick={() => {
          triggerHaptic('light');
          onChange(value + 1);
        }}
        className="w-7 h-7 rounded-lg bg-lime-400 text-black font-bold flex items-center justify-center hover:bg-lime-300 shadow-sm"
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

